import { consola } from 'consola'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

// Инициализируем плагины dayjs
dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * Константы для работы с датами
 */
export const DATE_CONSTANTS = {
  MOSCOW_TIMEZONE: 'Europe/Moscow',
  PAYMENT_TIMEOUT_MINUTES: 15,
} as const

/**
 * Преобразует UTC время в московское время
 * @param utcDate - дата в UTC формате (ISO string)
 * @returns объект с московским временем
 */
export function convertToMoscowTime(utcDate: string): {
  moscowTime: dayjs.Dayjs
  moscowISO: string
  moscowFormatted: string
  originalUTC: string
} {
  const utcMoment = dayjs.utc(utcDate)
  const moscowMoment = utcMoment.tz(DATE_CONSTANTS.MOSCOW_TIMEZONE)
  
  return {
    moscowTime: moscowMoment,
    moscowISO: moscowMoment.toISOString(),
    moscowFormatted: moscowMoment.format('DD.MM.YYYY HH:mm:ss'),
    originalUTC: utcDate,
  }
}

/**
 * Преобразует UTC время в московское время (простая версия)
 * @param utcDate - дата в UTC формате (ISO string)
 * @returns строка даты в московском времени
 */
export function convertUtcToMoscowTime(utcDate: string): string {
  if (!utcDate) return ''
  return dayjs.utc(utcDate).tz(DATE_CONSTANTS.MOSCOW_TIMEZONE).toISOString()
}

/**
 * Проверяет, является ли заказ свежим (создан в последние 15 минут)
 * @param createdAt - дата создания в UTC
 * @returns true если заказ свежий
 */
export function isOrderRecent(createdAt: string): boolean {
  const utcMoment = dayjs.utc(createdAt)
  const now = dayjs.utc()
  const fifteenMinutesAgo = now.subtract(DATE_CONSTANTS.PAYMENT_TIMEOUT_MINUTES, 'minutes')
  
  return utcMoment.isAfter(fifteenMinutesAgo)
}

/**
 * Вычисляет оставшееся время до истечения заказа
 * @param createdAt - дата создания в UTC
 * @returns информация о времени истечения
 */
export function calculateOrderExpiration(createdAt: string): {
  isExpired: boolean
  remainingTimeMs: number
  remainingMinutes: number
  expiresAt: dayjs.Dayjs
  createdAt: dayjs.Dayjs
} {
  const utcMoment = dayjs.utc(createdAt)
  const expiresAt = utcMoment.add(DATE_CONSTANTS.PAYMENT_TIMEOUT_MINUTES, 'minutes')
  const now = dayjs.utc()
  const remainingTimeMs = expiresAt.diff(now)
  
  return {
    isExpired: remainingTimeMs <= 0,
    remainingTimeMs: Math.max(0, remainingTimeMs),
    remainingMinutes: Math.floor(remainingTimeMs / 60_000),
    expiresAt,
    createdAt: utcMoment,
  }
}

/**
 * Форматирует оставшееся время в MM:SS
 * @param remainingTimeMs - оставшееся время в миллисекундах
 * @returns отформатированная строка
 */
export function formatRemainingTime(remainingTimeMs: number): string {
  if (remainingTimeMs <= 0) return '00:00'
  
  const minutes = Math.floor(remainingTimeMs / 60_000)
  const seconds = Math.floor((remainingTimeMs % 60_000) / 1000)
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

/**
 * Логирует информацию о времени заказа для отладки
 * @param orderId - ID заказа
 * @param createdAt - дата создания в UTC
 */
export function logOrderTimeInfo(orderId: string, createdAt: string): void {
  const moscowTime = convertToMoscowTime(createdAt)
  const expiration = calculateOrderExpiration(createdAt)
  
  consola.debug(`⏰ Order #${orderId} time info:`, {
    originalUTC: createdAt,
    moscowTime: moscowTime.moscowFormatted,
    isRecent: isOrderRecent(createdAt),
    isExpired: expiration.isExpired,
    remainingMinutes: expiration.remainingMinutes,
    expiresAtUTC: expiration.expiresAt.toISOString(),
    expiresAtMoscow: expiration.expiresAt.tz(DATE_CONSTANTS.MOSCOW_TIMEZONE).format('DD.MM.YYYY HH:mm:ss'),
  })
}
